import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import { Icon24Clear } from '@figma/fpl-icons/icon-24-clear';
import { Icon24Search } from '@figma/fpl-icons/icon-24-search';
import clsx from 'clsx';
import { ButtonPrimitive } from '../button-primitive/button-primitive.js';
import { useLazyInput } from '../hooks/use-lazy-input.js';
import { useSharedRef } from '../hooks/use-shared-ref.js';
import { Input } from '../input/input.js';
import { useString } from '../settings/settings.strings.js';
import { accept, preventDefault } from '../utils/event.js';
import { icon, size, search, root, borderless, clear } from './search-input.css.js';

const VARIANT_MAP = {
    default: undefined,
    borderless: borderless,
};
const _SearchInput = forwardRef(({ onKeyDown, 
// Force a placeholder value to make css `:placeholder-shown` work reliably. An empty string
// works for Chrome and Firefox, but not Safari. The unicode character chosen is obscure and
// doesn't have a name, so it doesn't get announced by VO in Safari. The voiceover text box
// does show a [?] icon but that seems okay to me.
placeholder = '\u2065', size: size$1 = 'md', variant = 'default', noClearButton = false, ...props }, refIn) => {
    const ref = useSharedRef(refIn);
    const clear = (event) => {
        props.onChange?.('', { event, clear: true });
        ref.current.value = '';
    };
    return (jsxs(Input.Root, { "data-fpl-component": "", size: size$1, ...{ className: clsx(root, VARIANT_MAP[variant]) }, children: [jsx("div", { "aria-hidden": true, className: clsx(icon, size[size$1]), children: jsx(Icon24Search, {}) }), jsx(Input, { ...props, ref: ref, 
                // In testing we found that `type="search"` doesn't render consistently across browsers,
                // and it also has no real effect on screen readers, so we use `type="text"` with role.
                type: "text", role: "searchbox", placeholder: placeholder, enterKeyHint: "search", onKeyDown: (e) => {
                    onKeyDown?.(e);
                    // Chrome and Safari clear the input when Escape is pressed natively, but Firefox
                    // does not, so we implement it ourselves.
                    if (e.key === 'Escape' && e.currentTarget.value) {
                        accept(e);
                        clear(e);
                    }
                }, ...{ className: search } }), !props.disabled && !noClearButton && jsx(ClearSearchButton, { size: size$1, onClick: clear })] }));
});
_SearchInput.displayName = 'SearchInput';
/**
 * Make a search input keep track of its own state for a short time while editing. Intended for controlled
 * inputs that may be slow to update, due to the state update triggering a big re-render.
 *
 * Lazy inputs should be preferred when the input does not need to modify the value in any way. If
 * the value needs to be modified (e.g. trimming whitespace), then the input should not use this
 * hook. Instead it can use a regular Input with a controlled value, or defer triggering onChange
 * to a later time, such as on blur.
 * *
 * Must be used on a controlled input, so value and onChange are required.
 */
const Lazy = forwardRef((propsIn, ref) => {
    const props = useLazyInput(propsIn);
    return jsx(_SearchInput, { "data-fpl-component": "", ...props, ref: ref });
});
Lazy.displayName = 'SearchInput.Lazy';
/**
 * A simple search input. Sets `type="search"` and includes a search icon and clear button.
 *
 * @see https://fpl.figma.design/components/inputs/search/
 */
const SearchInput = Object.assign(_SearchInput, { Lazy });
// eslint-disable-next-line react/display-name
function ClearSearchButton({ size: size$1, onClick, }) {
    return (jsx(ButtonPrimitive, { "data-fpl-component": "", onClick: onClick, className: clsx(clear, size[size$1]), "aria-label": useString('clearSearch'), tabIndex: -1, 
        // allow the input to keep focus when clicking the clear button
        onPointerDown: preventDefault, children: jsx(Icon24Clear, {}) }));
}

export { SearchInput };
//# sourceMappingURL=search-input.js.map
